/**
 * D458 (UI-HANDOFF section 15): the parts of the Settings sheet. A group head, a 48px row with a
 * label and a control, a row that is one button with a chevron (Signed in, World name, How to
 * play), the split Sound row (the label opens the Sound sheet, the switch alone turns sound on
 * and off), and the Delete account ghost button. Kept apart from `IdleApp.tsx` so the bench can
 * read them.
 */
import { ChevronRight, KeyRound, Volume2 } from 'lucide-preact';
import type { ComponentChildren } from 'preact';
import { Button } from '../ui/Button.tsx';
import { Switch } from './Switch.tsx';

/** A row's name: 15px 600 in the UI font (`--fs-name`). */
export const SETTINGS_LABEL = { font: '600 var(--fs-name)/1.2 var(--font-ui)' };
/** The line under a row's name: 12px, `ink-2`. */
export const SETTINGS_NOTE = {
  font: '400 var(--fs-caption)/1.4 var(--font-ui)',
  color: 'var(--ink-2)',
};

/**
 * D459: the edge a screen keeps from the glass, 12px (`--edge` in tokens.css).
 */
export const EDGE = 'var(--edge)';

/**
 * D459 (section 17, Settings 1): Settings and Sound fill the width and the height with a grab
 * handle and no corner, rather than sit inset. Passed to the shared Sheet as its panel style.
 */
export const FULL_SHEET = {
  maxWidth: 'none',
  height: '100%',
  maxHeight: '100%',
  borderRadius: 0,
  padding: EDGE,
  // Rows keep their content height; the sheet scrolls rather than squashing a group.
  gridAutoRows: 'max-content',
  paddingBottom: `calc(${EDGE} + env(safe-area-inset-bottom))`,
};

/** D431 step 5: a quiet group head over a group of rows (12px 600 `ink-2`). */
export function SettingsHead({ title }: { title: string }) {
  return (
    <h3
      style={{
        margin: '6px 2px -2px',
        font: '600 var(--fs-caption)/1.2 var(--font-ui)',
        color: 'var(--ink-2)',
      }}
    >
      {title}
    </h3>
  );
}

/**
 * One row of a group: its label on the left and its control on the right. With `stack` the
 * control sits under the label at the row's full width (Map quality).
 */
export function SettingsRow({
  label,
  note,
  children,
  testId,
  stack = false,
}: {
  label: string;
  note?: string;
  children?: ComponentChildren;
  testId?: string;
  stack?: boolean;
}) {
  return (
    <div
      data-testid={testId}
      style={
        stack
          ? { display: 'grid', gap: 8, alignContent: 'center', padding: '10px 12px 12px' }
          : { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }
      }
    >
      {note ? (
        <span style={{ display: 'grid', gap: 2, minWidth: 0 }}>
          <span style={SETTINGS_LABEL}>{label}</span>
          <span style={SETTINGS_NOTE}>{note}</span>
        </span>
      ) : (
        // The label is the row's direct child, so a spec finds the row from it.
        <span style={SETTINGS_LABEL}>{label}</span>
      )}
      {children}
    </div>
  );
}

/** The right-hand chevron of a row that opens something. */
export function RowChevron() {
  return (
    <ChevronRight size={18} aria-hidden="true" style={{ color: 'var(--ink-2)', flex: 'none' }} />
  );
}

/**
 * A row that is one button: an optional icon in `accent`, the name over an optional note, a value
 * on the right, then a chevron. The whole row is the tap target, 48px or taller.
 */
export function SettingsNavRow({
  label,
  note,
  value,
  icon,
  onClick,
  testId,
  ariaLabel,
}: {
  label: string;
  note?: ComponentChildren;
  value?: ComponentChildren;
  icon?: ComponentChildren;
  onClick: () => void;
  testId?: string;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      class="idle-set-nav"
      data-testid={testId}
      aria-label={ariaLabel}
      onClick={onClick}
    >
      {icon}
      <span style={{ display: 'grid', gap: 2, minWidth: 0, flex: 1, textAlign: 'left' }}>
        <span style={SETTINGS_LABEL}>{label}</span>
        {note && <span style={SETTINGS_NOTE}>{note}</span>}
      </span>
      {value && (
        <span
          style={{
            font: '400 var(--fs-body)/1.3 var(--font-ui)',
            color: 'var(--ink-2)',
            minWidth: 0,
            maxWidth: '50%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </span>
      )}
      <RowChevron />
    </button>
  );
}

/** The key icon of the Signed in row, in `accent`. */
export function KeyIcon() {
  return <KeyRound size={20} aria-hidden="true" style={{ color: 'var(--accent)', flex: 'none' }} />;
}

/**
 * The Sound row, split in two by a 1px `line` divider. The left part (the speaker, "Sound" over
 * "Music and effects", a chevron) is one button that opens the Sound sheet; the switch alone, on
 * the right, is the master on and off.
 */
export function SoundRow({
  on,
  onToggle,
  onOpen,
}: {
  on: boolean;
  onToggle: (on: boolean) => void;
  onOpen: () => void;
}) {
  return (
    <div class="idle-set-split" data-testid="settings-sound">
      <button
        type="button"
        class="idle-set-nav"
        data-testid="settings-sound-open"
        aria-label="Sound settings"
        onClick={onOpen}
      >
        <Volume2 size={20} aria-hidden="true" style={{ color: 'var(--accent)', flex: 'none' }} />
        <span style={{ display: 'grid', gap: 2, minWidth: 0, flex: 1, textAlign: 'left' }}>
          <span style={SETTINGS_LABEL}>Sound</span>
          <span style={SETTINGS_NOTE}>Music and effects</span>
        </span>
        <RowChevron />
      </button>
      <span class="idle-set-split-switch">
        <Switch label="Sound" on={on} onChange={onToggle} />
      </span>
    </div>
  );
}

/**
 * Delete account: not a row but a full-width ghost button under the list (transparent, a 1px
 * `line` ring, `ink` text, 44px). Never red.
 */
export function GhostRingButton({
  children,
  onClick,
  testId,
}: {
  children: ComponentChildren;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <Button
      variant="ghost"
      full
      data-testid={testId}
      onClick={onClick}
      style={{ height: 44, color: 'var(--ink)', border: '1px solid var(--line)' }}
    >
      {children}
    </Button>
  );
}
